import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, GraduationCap, Globe, Utensils, Users, Heart, Award, MapPin, ShoppingCart, Sun, Clock } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import claraPortrait from "@/assets/clara-portrait-final.png";
import heroBackground from "@/assets/hero-background.jpg";

export const faqItems = [
  {
    question: "Você atende presencialmente em Brisbane?",
    answer: "Sim. Moro em Brisbane e atendo presencialmente no meu consultório em Hamilton. Também ofereço consultas online para quem prefere ou não pode se deslocar. A qualidade do atendimento é a mesma nos dois formatos."
  },
  {
    question: "Em quais regiões de Brisbane você atende?",
    answer: "O atendimento presencial é no consultório em Hamilton, na 8 Harbour Rd. Para quem mora na Gold Coast, na Sunshine Coast ou em subúrbios mais distantes, a consulta online funciona muito bem."
  },
  {
    question: "Os planos alimentares usam produtos de mercados de Brisbane?",
    answer: "Sim. Todos os planos são montados com produtos que você encontra no Coles, no Woolworths e no Aldi de Brisbane. Para ingredientes específicos, também considero lojas brasileiras como a Brazil at Home e a Saudade Brazilian Shop."
  },
  {
    question: "Como funciona a primeira consulta?",
    answer: "É uma conversa de cerca de 30 minutos, sem custo, por videochamada ou no consultório em Hamilton. Eu entendo seus objetivos e sua rotina em Brisbane e explico como funciona o acompanhamento. Depois você decide, com calma, se quer seguir."
  },
  {
    question: "Você conhece os desafios de quem mora em Brisbane?",
    answer: "Sim, porque eu também moro aqui. Sei como o calor subtropical afeta o apetite e conheço os mercados locais, os hábitos da comunidade brasileira e as dificuldades de manter uma alimentação saudável na cidade."
  },
  {
    question: "Quanto custa o acompanhamento?",
    answer: "Os valores variam conforme o tipo de acompanhamento. Na consulta inicial eu apresento as opções e os valores com transparência, e você escolhe o plano que faz mais sentido para você."
  },
  {
    question: "Todo o atendimento é em português?",
    answer: "Sim. Consultas, plano alimentar, receitas e mensagens são em português. Você não precisa traduzir nada."
  },
  {
    question: "Como funciona o suporte entre as consultas?",
    answer: "Você tem acesso à plataforma e contato direto comigo por WhatsApp. Dúvidas sobre o que pedir num restaurante ou adaptações no plano são resolvidas entre uma consulta e outra."
  },
];

const desafiosBrisbane = [
  {
    icon: Sun,
    title: "Clima subtropical e alimentação",
    description: "O calor de Brisbane muda o apetite, aumenta a necessidade de hidratação e pede refeições mais leves. Eu ajusto o seu plano ao clima daqui, especialmente nos meses mais quentes."
  },
  {
    icon: ShoppingCart,
    title: "Mercados e produtos locais",
    description: "Do Coles e do Woolworths a feiras como o Jan Powers Farmers Market, em New Farm, e o West End Markets, Brisbane tem muitas opções. O plano é montado com o que está disponível e cabe no seu orçamento."
  },
  {
    icon: Clock,
    title: "Rotina de trabalho e estudo",
    description: "Muitos brasileiros em Brisbane trabalham em hospitality ou na construção civil, ou estudam em tempo integral. Monto planos práticos para quem tem horários corridos e precisa de um meal prep eficiente."
  },
  {
    icon: MapPin,
    title: "Comunidade brasileira ativa",
    description: "Brisbane tem uma das maiores comunidades brasileiras da Austrália. Churrascos, eventos e restaurantes brasileiros fazem parte da rotina social, e eu ajudo você a equilibrar esses momentos com seus objetivos."
  },
];

const beneficios = [
  "Plano alimentar com produtos dos mercados de Brisbane (Coles, Woolworths, Aldi)",
  "Opções de receitas adaptadas ao clima subtropical de QLD",
  "Acesso à plataforma, com conteúdo prático",
  "Contato direto comigo por WhatsApp entre as consultas",
  "Ajustes conforme a sua rotina em Brisbane muda",
  "Consultas presenciais em Hamilton ou online",
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Brisbane, QLD
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Nutricionista brasileira em Brisbane
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Atendimento presencial em Hamilton, Brisbane, e online para qualquer cidade da Austrália. Plano alimentar personalizado com produtos que você encontra aqui, tudo em português.
              </p>
              <CTAButtons openDialog={openDialog} />
              <p className="text-sm text-muted-foreground font-sans">
                Consulta inicial de 30 minutos, sem custo.
              </p>
            </div>
          </section>

          {/* DESAFIOS DE BRISBANE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Vida em Brisbane
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Os desafios de comer bem morando em Brisbane
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {desafiosBrisbane.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CONTEÚDO LOCAL */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-8 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Entendendo sua realidade
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground leading-tight">
                  Alimentação saudável na rotina de Brisbane
                </h2>
              </div>

              {/* Intro text */}
              <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-3xl mx-auto text-center mb-8 sm:mb-12">
                Brisbane cresce rápido e recebe cada vez mais brasileiros. Quem chegou há pouco ou já mora aqui há anos sabe que a rotina alimentar é bem diferente da brasileira.
              </p>

              {/* Visual cards instead of wall of text */}
              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 max-w-3xl mx-auto">
                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Onde os brasileiros moram</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Southbank, West End, South Brisbane, Sunnybank, Woolloongabba: cada bairro tem sua rotina, e o plano considera os mercados e as opções da sua região.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <ShoppingCart className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Onde comprar bem</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Do Woolworths da Queen Street ao Aldi e ao mercado de Rocklea, o plano considera onde você já compra e ajuda a encontrar produtos frescos e boas ofertas.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Clock className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Plano para a sua rotina</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Trabalha no CBD, estuda na QUT ou na UQ, mora em Logan ou em North Lakes? Eu adapto o plano à <span className="italic gradient-text font-medium">sua</span> realidade.
                  </p>
                </div>

                <div className="rounded-xl sm:rounded-2xl bg-card border border-border/50 p-5 sm:p-6 space-y-2">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Sun className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-sm sm:text-base font-bold text-foreground font-sans">Vida social equilibrada</h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    Churrasco no fim de semana, açaí, comida brasileira: o plano abre espaço para esses momentos sem perder de vista os seus objetivos.
                  </p>
                </div>
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* O QUE VOCÊ RECEBE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que está incluído
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe no acompanhamento
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
                {beneficios.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* CREDENCIAIS */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Quem te acompanha
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Conheça a Clara Camille
                </h2>
              </div>

              <div
                className="rounded-2xl sm:rounded-3xl overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(16px) saturate(1.3)',
                  WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                }}
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-2/5 flex-shrink-0">
                    <div className="aspect-[3/4] sm:h-full w-full overflow-hidden">
                      <img
                        src={claraPortrait}
                        alt="Clara Camille, nutricionista brasileira em Brisbane"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="sm:w-3/5 p-6 sm:p-10 flex flex-col justify-center space-y-6">
                    <div className="space-y-1">
                      <h3 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                      <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Brisbane, QLD</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { icon: GraduationCap, text: "Formação em Nutrição" },
                        { icon: Globe, text: "Atendimento em português" },
                        { icon: MapPin, text: "Consultório em Hamilton, Brisbane" },
                      ].map((cred, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5">
                          <cred.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-xs sm:text-sm text-foreground font-sans leading-snug">{cred.text}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                      Moro em Brisbane e atendo brasileiros que querem cuidar da alimentação com alguém que conhece a rotina local. Meu trabalho é prático, em português e sem julgamento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Tire suas dúvidas
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Dúvidas frequentes sobre nutrição em Brisbane
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="rounded-xl border border-border/50 bg-background px-5 sm:px-6"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Mora em Brisbane? Vamos conversar
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, entendo sua rotina e seus objetivos e explico como o acompanhamento funciona. Pode ser no consultório em Hamilton ou online.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-na-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista na Austrália</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-sydney" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Sydney</Link>
                <span className="text-border">·</span>
                <Link to="/nutricionista-brasileira-melbourne" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Nutricionista em Melbourne</Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const NutricionistaBrasileiraBrisbane = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default NutricionistaBrasileiraBrisbane;
